import React from 'react';
import type { DisplayMessage } from '../chatHelpers';

interface Result {
  highlightedId: string | null;
}

// Manages the `?highlight=<messageId>` URL behavior:
//   - When NOT highlighting, keeps the message list scrolled to the bottom.
//   - When highlighting, scrolls the target message into view, briefly flashes
//     a highlight class, then clears.
export function useHighlightScroll(
  displayMessages: DisplayMessage[],
  listRef: React.RefObject<HTMLDivElement>,
  highlightMessageId: string | null
): Result {
  const [highlightedId, setHighlightedId] = React.useState<string | null>(null);
  const highlightScrolledRef = React.useRef<string | null>(null);

  React.useLayoutEffect(() => {
    if (highlightMessageId) {
      return;
    }
    const el = listRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [displayMessages, highlightMessageId, listRef]);

  React.useEffect(() => {
    if (!highlightMessageId || displayMessages.length === 0) {
      return undefined;
    }
    if (highlightScrolledRef.current === highlightMessageId) {
      return undefined;
    }
    highlightScrolledRef.current = highlightMessageId;

    const raf = requestAnimationFrame(() => {
      const container = listRef.current;
      const target = container?.querySelector(
        `[data-message-id="${highlightMessageId}"]`
      ) as HTMLElement | null;
      if (!container || !target) {
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      container.scrollTop +=
        targetRect.top -
        containerRect.top -
        container.clientHeight / 2 +
        target.clientHeight / 2;
      setHighlightedId(highlightMessageId);
      setTimeout(() => setHighlightedId(null), 2500);
    });
    return () => cancelAnimationFrame(raf);
  }, [highlightMessageId, displayMessages, listRef]);

  return { highlightedId };
}
